import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppWindow,
  ArrowRight,
  Boxes,
  FileArchive,
  Pencil,
  Plus,
  RefreshCw,
  Smartphone,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { createApp, deleteApp, listApps, updateApp, uploadApk } from '../api/AppOtaApi';
import { appBasePath } from '../components/layout/DeploymentWorkspaceNav';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './AppUpdateApps.module.scss';

const PACKAGE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;

const formatFileSize = bytes => {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
};

function AppUpdateApps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [newPackageName, setNewPackageName] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [editingApp, setEditingApp] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listApps();
      setApps(response.data || []);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(`Could not load apps: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeDrawer = () => {
    if (busy || uploading) {
      return;
    }
    setDrawer(null);
    setNewPackageName('');
    setNewDisplayName('');
    setEditingApp(null);
    setEditDisplayName('');
    setUploadFile(null);
    setUploadProgress(0);
  };

  const trimmedPackage = newPackageName.trim();
  const packageValid = PACKAGE_NAME_PATTERN.test(trimmedPackage);

  const handleCreateApp = async () => {
    if (!packageValid) {
      return;
    }
    try {
      setBusy(true);
      clearMessages();
      await createApp(trimmedPackage, newDisplayName.trim() || null);
      setSuccessMessage(`${trimmedPackage} registered with a default release channel.`);
      setDrawer(null);
      setNewPackageName('');
      setNewDisplayName('');
      await loadApps();
    } catch (error) {
      setErrorMessage(`Could not register app: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = app => {
    setEditingApp(app);
    setEditDisplayName(app.display_name || '');
    setDrawer('edit-app');
  };

  const handleSaveDisplayName = async () => {
    if (!editingApp) {
      return;
    }
    try {
      setBusy(true);
      clearMessages();
      await updateApp(editingApp.package_name, editDisplayName.trim() || null);
      setSuccessMessage(`${editingApp.package_name} updated.`);
      setDrawer(null);
      setEditingApp(null);
      await loadApps();
    } catch (error) {
      setErrorMessage(`Could not update app: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteApp = async app => {
    try {
      setBusy(true);
      clearMessages();
      await deleteApp(app.package_name);
      setSuccessMessage(`${app.package_name} removed.`);
      setPendingDelete(null);
      await loadApps();
    } catch (error) {
      setErrorMessage(`Could not delete app: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      return;
    }
    try {
      setUploading(true);
      clearMessages();
      setUploadProgress(0);
      const response = await uploadApk(uploadFile, progressEvent => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });
      const uploaded = response.data || {};
      setSuccessMessage(
        uploaded.app_registered
          ? `${uploaded.package_name} registered from ${uploadFile.name}; its release channel targets code ${uploaded.versioncode}.`
          : `${uploadFile.name} added to the ${uploaded.package_name} library as code ${uploaded.versioncode}.`
      );
      setDrawer(null);
      setUploadFile(null);
      setUploadProgress(0);
      await loadApps();
    } catch (error) {
      setErrorMessage(`Could not upload APK: ${error.response?.data?.detail || error.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const sortedApps = useMemo(
    () =>
      [...apps].sort((a, b) => {
        if (a.is_default !== b.is_default) {
          return a.is_default ? -1 : 1;
        }
        return a.package_name.localeCompare(b.package_name);
      }),
    [apps]
  );

  const totals = useMemo(
    () =>
      apps.reduce(
        (sum, app) => ({
          channels: sum.channels + (app.channel_count || 0),
          devices: sum.devices + (app.reporting_device_count || 0),
          apks: sum.apks + (app.apk_count || 0),
        }),
        { channels: 0, devices: 0, apks: 0 }
      ),
    [apps]
  );

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>App deployment</span>
          <h1>Applications</h1>
          <p>
            Each Android package has its own channels, APK library, device assignments and adoption
            statistics. Pick an app to manage its rollout.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={loadApps}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? styles.spinning : ''} />
            {loading ? 'Refreshing' : 'Refresh'}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setDrawer('upload-apk')}
          >
            <UploadCloud size={16} /> Upload APK
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => setDrawer('create-app')}
          >
            <Plus size={16} /> Register app
          </button>
        </div>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <section className={styles.summary} aria-label="Deployment summary">
        <div>
          <span>Registered apps</span>
          <strong>{apps.length}</strong>
        </div>
        <div>
          <span>Rollout channels</span>
          <strong>{totals.channels}</strong>
        </div>
        <div>
          <span>Reporting devices</span>
          <strong>{totals.devices}</strong>
        </div>
        <div>
          <span>APK artifacts</span>
          <strong>{totals.apks}</strong>
        </div>
      </section>

      {sortedApps.length ? (
        <div className={styles.appGrid}>
          {sortedApps.map(app => {
            const canDelete = !app.is_default && app.apk_count === 0;
            return (
              <article className={styles.appCard} key={app.package_name}>
                <div className={styles.appTitle}>
                  <span className={styles.appIcon}>
                    <AppWindow size={18} />
                  </span>
                  <div>
                    <strong>{app.display_name || app.package_name}</strong>
                    <code>{app.package_name}</code>
                  </div>
                  <div className={styles.appTitleActions}>
                    {app.is_default && (
                      <em title="Serves clients that do not send a package name">Default</em>
                    )}
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => openEdit(app)}
                      aria-label={`Rename ${app.package_name}`}
                      title="Edit display name"
                    >
                      <Pencil size={15} />
                    </button>
                  </div>
                </div>

                <dl className={styles.appStats}>
                  <div>
                    <dt>
                      <Boxes size={14} /> Release target
                    </dt>
                    <dd>{app.release_versioncode ? `code ${app.release_versioncode}` : '—'}</dd>
                  </div>
                  <div>
                    <dt>
                      <Boxes size={14} /> Channels
                    </dt>
                    <dd>{app.channel_count}</dd>
                  </div>
                  <div>
                    <dt>
                      <FileArchive size={14} /> APKs
                    </dt>
                    <dd>{app.apk_count}</dd>
                  </div>
                  <div>
                    <dt>
                      <Smartphone size={14} /> Devices
                    </dt>
                    <dd>
                      {app.reporting_device_count}
                      <small> reporting · {app.assignment_count} assigned</small>
                    </dd>
                  </div>
                </dl>

                <div className={styles.appFooter}>
                  {!app.is_default && (
                    <button
                      type="button"
                      className={styles.dangerLink}
                      onClick={() => setPendingDelete(app)}
                      disabled={!canDelete}
                      title={
                        canDelete ? 'Delete app' : 'Delete its APK files before removing the app'
                      }
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                  <Link to={appBasePath(app.package_name)} className={styles.openLink}>
                    Manage rollout <ArrowRight size={15} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        !loading && (
          <div className={styles.emptyState}>
            <AppWindow size={27} />
            <strong>No apps registered</strong>
            <p>Register a package or upload an APK to get started.</p>
          </div>
        )
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete ${pendingDelete?.display_name || pendingDelete?.package_name}?`}
        description="The app registration will be removed. This cannot be undone."
        confirmLabel="Delete app"
        busyLabel="Deleting…"
        busy={busy}
        onConfirm={() => handleDeleteApp(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'create-app'}
        eyebrow="Applications"
        title="Register an app"
        description="Creates the app with an empty release channel. Uploading an APK registers its package automatically, so this is only needed to prepare an app ahead of its first build."
        onClose={closeDrawer}
        closeDisabled={busy}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleCreateApp}
              disabled={busy || !packageValid}
            >
              {busy ? 'Registering…' : 'Register app'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>Android package name</span>
            <input
              value={newPackageName}
              onChange={event => setNewPackageName(event.target.value)}
              placeholder="e.g. com.xxx.app.beta"
              autoFocus
            />
            {trimmedPackage && !packageValid && (
              <small className={styles.fieldError}>
                Use the applicationId from the app's build, e.g. com.example.app
              </small>
            )}
          </label>
          <label>
            <span>
              Display name <small>optional</small>
            </span>
            <input
              value={newDisplayName}
              onChange={event => setNewDisplayName(event.target.value)}
              placeholder="e.g. Beta App"
            />
          </label>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'edit-app'}
        eyebrow="Applications"
        title="Edit display name"
        description={editingApp?.package_name || ''}
        onClose={closeDrawer}
        closeDisabled={busy}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSaveDisplayName}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>Display name</span>
            <input
              value={editDisplayName}
              onChange={event => setEditDisplayName(event.target.value)}
              placeholder="Leave empty to show the package name"
              autoFocus
            />
          </label>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'upload-apk'}
        eyebrow="APK library"
        title="Upload an APK"
        description="The backend reads the package name from the file and files the APK under that app, registering it if needed."
        onClose={closeDrawer}
        closeDisabled={uploading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeDrawer}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleFileUpload}
              disabled={uploading || !uploadFile}
            >
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload APK'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label className={styles.fileDrop}>
            <UploadCloud size={28} />
            <span>{uploadFile ? uploadFile.name : 'Choose an APK file'}</span>
            <small>
              {uploadFile ? formatFileSize(uploadFile.size) : 'Only .apk files are accepted'}
            </small>
            <input
              type="file"
              accept=".apk"
              onChange={event => setUploadFile(event.target.files?.[0] || null)}
            />
          </label>
          {uploading && (
            <progress className={styles.progress} max="100" value={uploadProgress}>
              {uploadProgress}%
            </progress>
          )}
        </div>
      </ActionDrawer>
    </div>
  );
}

export default AppUpdateApps;
